import apiClient from '@/shared/services/apiClient'
import type {
  ApiResponse,
  QuestionResponse,
  QuestionMappingsResponse,
  BatchQuestionSummaryResponse,
  QuestionSummary,
  PageInfo,
  PyqExamsResponse,
} from '../types'

// Max question IDs accepted by GET /questions/batch
const BATCH_MAX_IDS = 100

class PYQService {
  private readonly questionsBaseUrl = '/api/v1/question-bank/questions'
  private readonly syllabusBaseUrl = '/api/v1/syllabus'

  /**
   * Step 1: Get question mappings by node code from Syllabus API
   * @param nodeCode - The node code (unit code) to fetch mappings for
   * @param includeDescendants - Whether to include questions from child nodes
   */
  async getQuestionMappingsByNode(
    nodeCode: string,
    includeDescendants: boolean = true
  ): Promise<ApiResponse<QuestionMappingsResponse>> {
    const response = await apiClient.get<ApiResponse<QuestionMappingsResponse>>(
      `${this.syllabusBaseUrl}/question-mappings/pyq-by-node/${nodeCode}`,
      {
        params: { includeDescendants }
      }
    )
    return response.data
  }

  /**
   * Step 2: Get questions by their IDs using batch API
   * @param questionIds - Array of question UUIDs (max 100)
   */
  async getQuestionsByIds(
    questionIds: string[]
  ): Promise<ApiResponse<BatchQuestionSummaryResponse>> {
    if (questionIds.length === 0) {
      return {
        success: true,
        message: 'No questions to fetch',
        data: { questions: [] },
        timestamp: new Date().toISOString()
      }
    }

    // Batch API accepts comma-separated UUIDs
    const response = await apiClient.get<ApiResponse<BatchQuestionSummaryResponse>>(
      `${this.questionsBaseUrl}/batch`,
      {
        params: { questionIds: questionIds.join(',') }
      }
    )
    return response.data
  }

  /**
   * Get summaries of every PYQ mapped to a syllabus node (and its descendants),
   * in mapping order. Splits the IDs into chunks to stay under the batch API's 100 ID limit.
   * @param nodeCode - Syllabus node code (unit or topic)
   */
  async getQuestionSummariesByNode(nodeCode: string): Promise<QuestionSummary[]> {
    const mappingsResponse = await this.getQuestionMappingsByNode(nodeCode, true)
    const questionIds = [...new Set(mappingsResponse.data.mappings.map((m) => m.questionId))]

    const chunks: string[][] = []
    for (let i = 0; i < questionIds.length; i += BATCH_MAX_IDS) {
      chunks.push(questionIds.slice(i, i + BATCH_MAX_IDS))
    }
    const responses = await Promise.all(chunks.map((ids) => this.getQuestionsByIds(ids)))

    // The batch API doesn't preserve request order, and drops IDs it can't find
    const byId = new Map(responses.flatMap((r) => r.data.questions).map((q) => [q.questionId, q]))
    return questionIds.flatMap((id) => byId.get(id) ?? [])
  }

  /**
   * Get a single question by ID (for full details including solution)
   * @param questionId - Question UUID
   */
  async getQuestionById(questionId: string): Promise<ApiResponse<QuestionResponse>> {
    const response = await apiClient.get<ApiResponse<QuestionResponse>>(
      `${this.questionsBaseUrl}/${questionId}`
    )
    return response.data
  }

  /**
   * Combined method: Get all question IDs for a unit, then fetch full questions
   * @param nodeCode - The unit node code
   * @param includeDescendants - Whether to include child nodes
   */
  async getQuestionsForUnit(
    nodeCode: string,
    includeDescendants: boolean = true
  ): Promise<{ questionIds: string[]; totalCount: number }> {
    // Step 1: Get mappings
    const mappingsResponse = await this.getQuestionMappingsByNode(nodeCode, includeDescendants)

    // Extract unique question IDs
    const questionIds = [...new Set(
      mappingsResponse.data.mappings.map(m => m.questionId)
    )]

    return {
      questionIds,
      totalCount: questionIds.length
    }
  }

  /**
   * Get PYQ questions by test code (exam)
   * @param testCode - The test code to search for (e.g., 'NEET_2024')
   * @param page - Page number (default 0)
   * @param size - Number of questions to fetch (default 200)
   */
  async getPyqExams(): Promise<ApiResponse<PyqExamsResponse>> {
    const response = await apiClient.get<ApiResponse<PyqExamsResponse>>(`${this.questionsBaseUrl}/pyq/exams`)
    return response.data
  }

  /**
   * Get PYQ questions for one exam and subject
   * @param testCode - Exam test code, e.g. "NEET_2024"
   * @param subject - Syllabus subject node code, e.g. "NEET_UG_PHY"
   */
  async searchPyqBySubject(
    testCode: string,
    subject: string,
    page: number = 0,
    size: number = 200
  ): Promise<{ content: QuestionResponse[]; page: PageInfo }> {
    const response = await apiClient.get<{ content: QuestionResponse[]; page: PageInfo }>(
      `${this.questionsBaseUrl}/pyq/by-subject`,
      {
        params: {
          testCode,
          subject,
          page,
          size,
        }
      }
    )
    return response.data
  }
}

export default new PYQService()
